import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useSWR from 'swr';

import { PlatformConfig, getPlatformConfig } from '@/service/platformConfig';

const PLATFORM_CONFIG_KEY = '/oauth/platformconfig';
const PLATFORM_CONFIG_CACHE_KEY = 'platform_config';

const getCachedPlatformConfig = (): PlatformConfig | undefined => {
  try {
    return JSON.parse(localStorage.getItem(PLATFORM_CONFIG_CACHE_KEY) || 'null') || undefined;
  } catch {
    return undefined;
  }
};

const cachedPlatformConfig = getCachedPlatformConfig();

/** 获取全局平台品牌配置，并统一应用浏览器标题与图标 */
export function usePlatformConfig() {
  const { t } = useTranslation();
  const { data, isLoading, mutate } = useSWR(PLATFORM_CONFIG_KEY, getPlatformConfig, {
    fallbackData: cachedPlatformConfig,
    shouldRetryOnError: false,
  });
  const platformConfig = {
    systemName: data?.systemName || t('login-page:brand.default.system.name'),
    recordNumber: data?.recordNumber || t('login-page:brand.default.record.number'),
    logoURL: data?.logoURL || '/images/logo.svg',
    faviconURL: data?.faviconURL || '/images/favicon.svg',
    footerLogoURL: data?.footerLogoURL ?? '/images/logo_light.svg',
    companyAddress: data?.companyAddress ?? t('home:5u5f72q'),
    footerSlogan: data?.footerSlogan ?? t('home:pin6ede'),
    contactPhone: data?.contactPhone ?? '021-61060282',
    investorPhone: data?.investorPhone ?? '021-80104277',
    contactEmail: data?.contactEmail ?? 'info@vastaitech.com',
    marketingEmail: data?.marketingEmail ?? 'marketing@vastaitech.com',
    recruitmentEmail: data?.recruitmentEmail ?? 'recruitment@vastaitech.com',
    investorEmail: data?.investorEmail ?? 'ir@vastaitech.com',
  };

  useEffect(() => {
    if (isLoading) return;
    document.title = platformConfig.systemName;
    try {
      localStorage.setItem(
        PLATFORM_CONFIG_CACHE_KEY,
        JSON.stringify({
          systemName: platformConfig.systemName,
          recordNumber: platformConfig.recordNumber,
          logoURL: platformConfig.logoURL,
          faviconURL: platformConfig.faviconURL,
          footerLogoURL: platformConfig.footerLogoURL,
          companyAddress: platformConfig.companyAddress,
          footerSlogan: platformConfig.footerSlogan,
          contactPhone: platformConfig.contactPhone,
          investorPhone: platformConfig.investorPhone,
          contactEmail: platformConfig.contactEmail,
          marketingEmail: platformConfig.marketingEmail,
          recruitmentEmail: platformConfig.recruitmentEmail,
          investorEmail: platformConfig.investorEmail,
        }),
      );
    } catch {
      // 浏览器禁用存储时仍可正常使用当前接口数据。
    }
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!icon) return;
    icon.onerror = () => {
      icon.href = '/images/favicon.svg';
    };
    icon.href = platformConfig.faviconURL;
  }, [
    isLoading,
    platformConfig.companyAddress,
    platformConfig.contactEmail,
    platformConfig.contactPhone,
    platformConfig.faviconURL,
    platformConfig.footerLogoURL,
    platformConfig.footerSlogan,
    platformConfig.investorEmail,
    platformConfig.investorPhone,
    platformConfig.logoURL,
    platformConfig.marketingEmail,
    platformConfig.recordNumber,
    platformConfig.recruitmentEmail,
    platformConfig.systemName,
  ]);

  const refreshPlatformConfig = async () => {
    const next = await mutate();
    if (!next) return;
    const timestamp = Date.now();
    await mutate(
      {
        ...next,
        logoURL: next.logoURL ? `/oauth/logo?t=${timestamp}` : next.logoURL,
        faviconURL: next.faviconURL ? `/oauth/favicon?t=${timestamp}` : next.faviconURL,
        footerLogoURL: next.footerLogoURL ? `/oauth/footer-logo?t=${timestamp}` : next.footerLogoURL,
      },
      false,
    );
  };

  return { platformConfig, isLoading, refreshPlatformConfig };
}
